import React, { useState, useEffect } from 'react';
import { getDatabase, ref, onValue, query, orderByChild, equalTo } from "firebase/database";
import { getAuth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import DashboardHeader from '../../AdminComponents/DashboardHeader';
import GenerativeOrbAnimation from '../../Pages/GenerativeOrbAnimation';
import EmailFlowBuilder from '../Components/EmailFlowBuilder';
import EmailTemplatesList from '../Components/EmailTemplatesList';
import EmailSenderSettings from '../Components/EmailSenderSettings';
import './EmailFlowDashboard.css';

const EmailFlowDashboard = () => {
    const [emailFlows, setEmailFlows] = useState([]);
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeView, setActiveView] = useState('flows'); // 'flows', 'templates', or 'senders'
    const [showCreateFlow, setShowCreateFlow] = useState(false);
    const [showSenderSettings, setShowSenderSettings] = useState(false);
    const [emailSenders, setEmailSenders] = useState([]);

    const db = getDatabase();
    const auth = getAuth();
    const currentUser = auth.currentUser;
    const navigate = useNavigate();

    // Load user's email flows
    useEffect(() => {
        if (currentUser) {
            const flowsQuery = query(
                ref(db, 'emailFlows'),
                orderByChild('userId'),
                equalTo(currentUser.uid)
            );

            onValue(flowsQuery, (snapshot) => {
                if (snapshot.exists()) {
                    const flowsData = [];
                    snapshot.forEach((childSnapshot) => {
                        flowsData.push({
                            id: childSnapshot.key,
                            ...childSnapshot.val()
                        });
                    });
                    setEmailFlows(flowsData.sort((a, b) => b.createdAt - a.createdAt));
                } else {
                    setEmailFlows([]);
                }
            });

            // Load email templates
            const templatesQuery = query(
                ref(db, 'emailTemplates'),
                orderByChild('userId'),
                equalTo(currentUser.uid)
            );

            onValue(templatesQuery, (snapshot) => {
                if (snapshot.exists()) {
                    const templatesData = [];
                    snapshot.forEach((childSnapshot) => {
                        templatesData.push({
                            id: childSnapshot.key,
                            ...childSnapshot.val()
                        });
                    });
                    setEmailTemplates(templatesData.sort((a, b) => b.createdAt - a.createdAt));
                } else {
                    setEmailTemplates([]);
                }
            });

            // Load email senders
            const sendersRef = ref(db, `emailSenders/${currentUser.uid}`);
            onValue(sendersRef, (snapshot) => {
                if (snapshot.exists()) {
                    const sendersData = [];
                    snapshot.forEach((childSnapshot) => {
                        sendersData.push({
                            id: childSnapshot.key,
                            ...childSnapshot.val()
                        });
                    });
                    setEmailSenders(sendersData.sort((a, b) => b.createdAt - a.createdAt));
                } else {
                    setEmailSenders([]);
                }
                setLoading(false);
            });
        }
    }, [currentUser, db]);

    const handleCreateFlow = () => {
        setShowCreateFlow(true);
    };

    const handleCreateTemplate = () => {
        // Check if user has email senders configured
        if (emailSenders.length === 0) {
            alert('Bitte erstellen Sie zuerst einen Email-Absender, bevor Sie Templates erstellen können.');
            setShowSenderSettings(true);
            return;
        }
        navigate('/emailflow/template/new');
    };

    if (loading) {
        return (
            <div className="loading-spinner">
                <h1>BonusClub</h1>
                <div className="spinner-circle"></div>
            </div>
        );
    }

    return (
        <div>
            <DashboardHeader />
            <GenerativeOrbAnimation />
            
            <div className="email-flow-dashboard-container">
                <div className="email-flow-header">
                    <div className="email-flow-title">
                        <h1>Email Flow System</h1>
                        <p>Create automated email sequences and beautiful email templates</p>
                    </div>
                    
                    <div className="email-flow-actions">
                        <button 
                            className="email-flow-btn secondary"
                            onClick={() => setShowSenderSettings(true)}
                        >
                            <span>📧</span>
                            Manage Senders
                        </button>
                        <button 
                            className="email-flow-btn secondary"
                            onClick={handleCreateTemplate}
                        >
                            <span>📝</span>
                            Create Template
                        </button>
                        <button 
                            className="email-flow-btn primary"
                            onClick={handleCreateFlow}
                        >
                            <span>⚡</span>
                            Create Email Flow
                        </button>
                    </div>
                </div>

                {/* View Toggle */}
                <div className="email-flow-tabs">
                    <button 
                        className={`email-flow-tab ${activeView === 'flows' ? 'active' : ''}`}
                        onClick={() => setActiveView('flows')}
                    >
                        <span className="email-tab-icon">⚡</span>
                        Email Flows ({emailFlows.length})
                    </button>
                    <button 
                        className={`email-flow-tab ${activeView === 'templates' ? 'active' : ''}`}
                        onClick={() => setActiveView('templates')}
                    >
                        <span className="email-tab-icon">📧</span>
                        Email Templates ({emailTemplates.length})
                    </button>
                    <button 
                        className={`email-flow-tab ${activeView === 'senders' ? 'active' : ''}`}
                        onClick={() => setActiveView('senders')}
                    >
                        <span className="email-tab-icon">👤</span>
                        Email Senders ({emailSenders.length})
                    </button>
                </div>

                {/* Content */}
                <div className="email-flow-content">
                    {activeView === 'flows' && (
                        <div className="email-flows-section">
                            {emailFlows.length === 0 ? (
                                <div className="email-flow-empty">
                                    <div className="empty-icon">⚡</div>
                                    <h3>No Email Flows Yet</h3>
                                    <p>Create your first automated email sequence to engage with your customers.</p>
                                    <div className="empty-actions">
                                        <button 
                                            className="email-flow-btn primary"
                                            onClick={handleCreateFlow}
                                        >
                                            Create Your First Flow
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="email-flows-grid">
                                    {emailFlows.map(flow => (
                                        <div key={flow.id} className="email-flow-card">
                                            <div className="flow-card-header">
                                                <h4>{flow.name}</h4>
                                                <span className={`flow-status ${flow.status}`}>
                                                    {flow.status === 'active' ? '🟢' : '🔴'} {flow.status}
                                                </span>
                                            </div>
                                            <p>{flow.description}</p>
                                            <div className="flow-stats">
                                                <span>{flow.emailCount || 0} emails</span>
                                                <span>{flow.triggerCount || 0} triggers</span>
                                            </div>
                                            <div className="flow-actions">
                                                <button onClick={() => navigate(`/emailflow/flow/${flow.id}`)}>
                                                    Edit Flow
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeView === 'templates' && (
                        <EmailTemplatesList 
                            templates={emailTemplates}
                            onCreateTemplate={handleCreateTemplate}
                            onEditTemplate={(templateId) => navigate(`/emailflow/template/${templateId}`)}
                        />
                    )}

                    {activeView === 'senders' && (
                        <div className="email-senders-section">
                            {emailSenders.length === 0 ? (
                                <div className="email-flow-empty">
                                    <div className="empty-icon">👤</div>
                                    <h3>No Email Senders</h3>
                                    <p>Create your first email sender to start sending email templates.</p>
                                    <div className="empty-actions">
                                        <button 
                                            className="email-flow-btn primary"
                                            onClick={() => setShowSenderSettings(true)}
                                        >
                                            Create First Sender
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="email-senders-grid">
                                    {emailSenders.map(sender => (
                                        <div key={sender.id} className={`email-sender-card ${sender.isDefault ? 'default' : ''}`}>
                                            <div className="sender-card-header">
                                                <div className="sender-info">
                                                    <h4>{sender.name}</h4>
                                                    <p className="sender-email">{sender.email}</p>
                                                    {sender.replyTo && (
                                                        <p className="sender-reply">Reply: {sender.replyTo}</p>
                                                    )}
                                                </div>
                                                {sender.isDefault && (
                                                    <div className="default-badge">⭐ Default</div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                    
                                    <div className="add-sender-card" onClick={() => setShowSenderSettings(true)}>
                                        <div className="add-sender-content">
                                            <span className="add-icon">➕</span>
                                            <h4>Add New Sender</h4>
                                            <p>Configure another email sender</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {showCreateFlow && (
                    <EmailFlowBuilder 
                        onClose={() => setShowCreateFlow(false)}
                        onFlowCreated={(flowId) => {
                            setShowCreateFlow(false);
                            navigate(`/emailflow/flow/${flowId}`);
                        }}
                    />
                )}

                {showSenderSettings && (
                    <EmailSenderSettings 
                        onClose={() => setShowSenderSettings(false)}
                        onSenderSelected={() => setShowSenderSettings(false)}
                    />
                )}
            </div>
        </div>
    );
};

export default EmailFlowDashboard;
